import { z } from "zod";
import { localDateSchema, moneySchema, quantitySchema, timestampSchema, unitPriceSchema, uuidSchema, } from "../common/primitives.js";
import { dashboardAlarmsSchema, todaySalesSchema } from "../dashboard/dashboard.js";
import { reportSalesUnitSchema, reportVolumeUnitSchema } from "./common.js";
/**
 * Pano uçları (Prompt 22, ADR 0022 §7–11). Bölüm izni yoksa bölüm `null` (izinsiz bölüm hiç hesaplanmaz). Oranlar yüzde,
 * tek ondalık, işaretli dizge; payda 0 ise null ("hesaplanamaz").
 */
const percentSchema = z
    .string()
    .nullable()
    .describe('Yüzde (1 ondalık, işaretli dizge, ör. "12.5"); payda 0 ise null');
/** Dönem sorgusu: iki uç dahil, en çok 366 gün; `companyId` / istasyon süzgeci isteğe bağlı (kapsam yine yetkiden). */
export const REPORT_RANGE_MAX_DAYS = 366;
const rangeShape = {
    from: localDateSchema,
    to: localDateSchema.describe("Dâhil"),
};
const rangeRefine = (value) => value.from <= value.to;
const rangeMessage = { message: "Başlangıç tarihi bitişten sonra olamaz", path: ["from"] };
const stationIdsSchema = z
    .string()
    .optional()
    .describe("Virgülle ayrılmış istasyon kimlikleri (boş = yetkili tüm istasyonlar)")
    .transform((value) => (value ? value.split(",").filter(Boolean) : []))
    .pipe(z.array(uuidSchema).max(200));
export const reportRangeQuerySchema = z
    .object({ ...rangeShape, companyId: uuidSchema.optional(), stationIds: stationIdsSchema })
    .refine(rangeRefine, rangeMessage);
// ── Canlı bugün ──────────────────────────────────────────────────────────────
export const LIVE_SOURCES = ["LIVE", "SUMMARY"];
export const liveSourceSchema = z
    .enum(LIVE_SOURCES)
    .describe("LIVE = Redis canlı sayaç; SUMMARY = veritabanı özeti (yedek)");
export const liveUnitSchema = z.object({
    unit: reportSalesUnitSchema,
    revenue: moneySchema,
    volume: quantitySchema.nullable(),
    volumeUnit: reportVolumeUnitSchema.nullable(),
    transactionCount: z.number().int().nonnegative(),
});
export const liveTodayStationSchema = z.object({
    stationId: uuidSchema,
    stationCode: z.string(),
    stationName: z.string(),
    revenue: moneySchema,
    litres: quantitySchema,
    units: z.array(liveUnitSchema),
    updatedAt: timestampSchema.nullable(),
});
export const liveTodaySchema = z.object({
    date: localDateSchema,
    asOf: timestampSchema,
    source: liveSourceSchema,
    revenue: moneySchema,
    litres: quantitySchema,
    stations: z.array(liveTodayStationSchema),
});
// ── Ana sayfa (istisna odaklı) ───────────────────────────────────────────────
export const HOME_TANK_LEVELS = ["LOW", "CRITICAL", "RUNNING_OUT"];
export const homeTankLevelSchema = z.enum(HOME_TANK_LEVELS);
export const HOME_TANK_LEVEL_LABELS = {
    LOW: "Düşük seviye",
    CRITICAL: "Kritik seviye",
    RUNNING_OUT: "24 saatte bitecek",
};
export const homeCriticalTankSchema = z.object({
    tankId: uuidSchema,
    stationId: uuidSchema,
    stationCode: z.string(),
    label: z.string(),
    productName: z.string(),
    fillPercent: z.number().int().min(0).max(100).nullable(),
    volumeL: quantitySchema.nullable(),
    estimatedRunOutAt: timestampSchema.nullable(),
    level: homeTankLevelSchema,
});
export const homeOfflineAgentSchema = z.object({
    stationId: uuidSchema,
    stationCode: z.string(),
    stationName: z.string(),
    lastSeenAt: timestampSchema.nullable(),
});
export const homeUnclosedDaySchema = z.object({
    stationId: uuidSchema,
    stationCode: z.string(),
    businessDate: localDateSchema,
});
/** Prompt 23: SLA'sı aşılmış açık bakım iş emri (en eski son an önce). */
export const homeSlaWorkOrderSchema = z.object({
    workOrderId: uuidSchema,
    workOrderNo: z.string(),
    stationId: uuidSchema,
    stationCode: z.string(),
    title: z.string(),
    priority: z.enum(["LOW", "NORMAL", "HIGH", "URGENT"]),
    breach: z.enum(["RESPONSE", "RESOLVE"]).describe("Çözüm aşımı yanıt aşımından önce gelir"),
    dueAt: timestampSchema,
});
/** Prompt 23: süresi dolmuş ya da 7 gün içinde dolacak uyum belgesi (en yakın bitiş önce). */
export const homeExpiringDocumentSchema = z.object({
    documentId: uuidSchema,
    stationId: uuidSchema.nullable(),
    stationCode: z.string().describe("Şirket düzeyi belgede şirket adı"),
    label: z.string(),
    expiresOn: localDateSchema,
    daysLeft: z.number().int(),
});
export const HOME_DOCUMENT_WINDOW_DAYS = 7;
export const homeTrendDaySchema = z.object({
    date: localDateSchema,
    revenue: moneySchema,
    litres: quantitySchema,
});
export const reportsHomeSchema = z.object({
    date: localDateSchema,
    asOf: timestampSchema,
    /** Sorunlar ÖNCE (ADR 0022 §7): her liste en önemliden; izin yoksa null. */
    issues: z.object({
        alarms: dashboardAlarmsSchema.nullable(),
        pendingApprovals: z.number().int().nonnegative().nullable(),
        criticalTanks: z.array(homeCriticalTankSchema).nullable(),
        offlineAgents: z.array(homeOfflineAgentSchema).nullable(),
        unclosedDays: z.array(homeUnclosedDaySchema).nullable(),
        slaBreachedWorkOrders: z.array(homeSlaWorkOrderSchema).nullable(),
        expiringDocuments: z.array(homeExpiringDocumentSchema).nullable(),
    }),
    /** Bugünün özeti: canlı sayaç (yoksa özet) + dün / geçen hafta aynı saate kadar. */
    today: todaySalesSchema.extend({ source: liveSourceSchema }).nullable(),
    /** Son 14 günün (bugün hariç) günlük cirosu ve litresi. */
    trend: z.array(homeTrendDaySchema).nullable(),
});
// ── İstasyon karşılaştırma ───────────────────────────────────────────────────
/**
 * İstasyon satırı. Her gösterge grubu kaynağının izniyle: satış (`fuel.ops.read` / `pos.read` …), akaryakıt ve fire
 * (`fuel.ops.read`), market (`pos.read`), yıkama (`wash.read`), şarj (`ev.read`), vardiya (`shifts.read`); izin yoksa null.
 */
export const stationComparisonRowSchema = z.object({
    stationId: uuidSchema.nullable().describe("Toplam satırında null"),
    stationCode: z.string().nullable(),
    stationName: z.string(),
    revenue: moneySchema.nullable(),
    revenueNet: moneySchema.nullable(),
    fuelLitres: quantitySchema.nullable(),
    fuelTransactions: z.number().int().nonnegative().nullable(),
    marketReceipts: z.number().int().nonnegative().nullable(),
    marketConversionRate: percentSchema.describe("Market fişi / pompa işlemi × 100"),
    washCycles: z.number().int().nonnegative().nullable(),
    washCyclesPerUnitDay: z.string().nullable().describe("Ünite başına günlük yıkama (2 ondalık)"),
    evSessions: z.number().int().nonnegative().nullable(),
    evEnergyKwh: quantitySchema.nullable(),
    evUtilization: percentSchema.describe("Şarj süresi / (konnektör × dönem) × 100"),
    shiftShortage: moneySchema.nullable(),
    shiftSurplus: moneySchema.nullable(),
    shiftOutOfTolerance: z.number().int().nonnegative().nullable(),
    fuelLossLitres: quantitySchema.nullable(),
    lossRate: percentSchema.describe("Onaylı fire / satış litresi × 100 (İKİ ondalık)"),
});
export const stationComparisonSchema = z.object({
    from: localDateSchema,
    to: localDateSchema,
    rows: z.array(stationComparisonRowSchema),
    total: stationComparisonRowSchema,
    /** Günlük özetin hesaplandığı son an (projeksiyon tazeliği). */
    refreshedAt: timestampSchema.nullable(),
});
// ── Akaryakıt analitiği ──────────────────────────────────────────────────────
/**
 * Akaryakıt analitiği en çok 92 günlük (çeyrek) dönem: yoğun saat ısı haritası ve tabanca hacmi saatlik veriden hesaplanır;
 * yıllık eğilim istasyon karşılaştırma ve günlük satış raporlarındadır (ADR 0022 §18, docs/performance/reporting.md).
 */
export const FUEL_ANALYTICS_MAX_DAYS = 92;
export const fuelAnalyticsQuerySchema = z
    .object({
    ...rangeShape,
    companyId: uuidSchema.optional(),
    stationId: uuidSchema.optional(),
    windowDays: z.coerce
        .number()
        .int()
        .min(1)
        .max(14)
        .default(3)
        .describe("Fiyat değişimi öncesi / sonrası gün"),
})
    .refine(rangeRefine, rangeMessage);
export const fuelNozzleVolumeSchema = z.object({
    nozzleId: uuidSchema,
    stationId: uuidSchema,
    stationCode: z.string(),
    label: z.string().describe('Örn. "Pompa 2 · Tabanca 1"'),
    productName: z.string(),
    litres: quantitySchema,
    amount: moneySchema,
    transactions: z.number().int().nonnegative(),
});
export const fuelHeatmapCellSchema = z.object({
    weekday: z.number().int().min(1).max(7),
    hour: z.number().int().min(0).max(23),
    litres: quantitySchema,
    averageLitres: quantitySchema,
    transactions: z.number().int().nonnegative(),
});
export const fuelProductMixSchema = z.object({
    productId: uuidSchema,
    productName: z.string(),
    unit: z.enum(["FUEL", "LPG"]),
    litres: quantitySchema,
    amount: moneySchema,
    share: percentSchema.describe("Litre payı"),
});
export const fuelPriceImpactSchema = z.object({
    id: uuidSchema,
    stationId: uuidSchema,
    stationCode: z.string(),
    productId: uuidSchema,
    productName: z.string(),
    effectiveDate: localDateSchema,
    oldPrice: unitPriceSchema.nullable(),
    newPrice: unitPriceSchema,
    priceChangePercent: percentSchema,
    beforeAverageLitres: quantitySchema.nullable(),
    afterAverageLitres: quantitySchema.nullable(),
    volumeChangePercent: percentSchema,
    partial: z.boolean(),
});
export const fuelAnalyticsSchema = z.object({
    from: localDateSchema,
    to: localDateSchema,
    windowDays: z.number().int(),
    nozzles: z.array(fuelNozzleVolumeSchema),
    heatmap: z.array(fuelHeatmapCellSchema),
    productMix: z.array(fuelProductMixSchema),
    priceChanges: z.array(fuelPriceImpactSchema),
});
// ── Nakit ve finans ──────────────────────────────────────────────────────────
export const financeOverviewQuerySchema = z.object({ companyId: uuidSchema.optional() });
export const financeOverviewSchema = z.object({
    companyId: uuidSchema,
    companyName: z.string(),
    asOf: localDateSchema,
    cash: z
        .object({
        cash: moneySchema,
        bank: moneySchema,
        bankAvailable: moneySchema.describe("Banka bakiyesi + kullanılabilir KMH"),
        posPending: moneySchema,
        netToday: moneySchema,
    })
        .nullable(),
    cheques: z
        .object({
        receivableOpen: moneySchema,
        endorsedOpen: moneySchema,
        problemOpen: moneySchema,
        issuedOpen: moneySchema,
        maturity: z.array(z.object({ bucket: z.string(), label: z.string(), receivable: moneySchema, payable: moneySchema })),
    })
        .nullable(),
    receivables: z
        .object({
        bucketLabels: z.array(z.string()),
        totals: z.array(moneySchema),
        grandTotal: moneySchema,
        top: z.array(z.object({ counterpartyId: uuidSchema, name: z.string(), total: moneySchema })),
    })
        .nullable(),
});
// ── Özet ↔ ana defter tutarlılığı ────────────────────────────────────────────
export const CONSISTENCY_STATUSES = ["MATCH", "UNPOSTED", "LEDGER_ONLY", "DIFFERENCE"];
export const consistencyStatusSchema = z.enum(CONSISTENCY_STATUSES);
export const CONSISTENCY_STATUS_LABELS = {
    MATCH: "Tutarlı",
    UNPOSTED: "Fişlenmemiş",
    LEDGER_ONLY: "Yalnızca defterde",
    DIFFERENCE: "Fark var",
};
export const consistencyQuerySchema = z
    .object({ ...rangeShape, companyId: uuidSchema })
    .refine(rangeRefine, rangeMessage);
export const consistencyRowSchema = z.object({
    stationId: uuidSchema.nullable(),
    stationName: z.string().nullable(),
    unit: reportSalesUnitSchema.nullable(),
    operationalRevenue: moneySchema.describe("KDV dahil"),
    operationalNet: moneySchema.describe("KDV hariç — defterle karşılaştırılan"),
    ledgerRevenue: moneySchema,
    difference: moneySchema.describe("Defter − operasyonel"),
    differencePercent: percentSchema,
    status: consistencyStatusSchema,
    /** Operasyonel cirosu olup defterde o güne gelir fişi OLMAYAN gün sayısı (gün sonu / fiş eksiği). */
    unpostedDays: z.number().int().nonnegative(),
});
export const consistencyReportSchema = z.object({
    companyId: uuidSchema,
    from: localDateSchema,
    to: localDateSchema,
    includesDrafts: z.boolean(),
    rows: z.array(consistencyRowSchema),
    totals: z.object({ operationalNet: moneySchema, ledgerRevenue: moneySchema, difference: moneySchema }),
    /** KDV oranı bilinmeyen kaynak içeren gün var mı (operasyonel KDV hariç yaklaşık). */
    vatRateMissing: z.boolean(),
    notes: z.array(z.string()),
});
//# sourceMappingURL=panels.js.map